// File: CarConfigurator - entry router (model splash, legacy vs dynamic)
// Created: 2026-05-29
// Updated: 2026-09-25
// Author: Erick Hernández Silva

import React, { useCallback, useEffect, useState } from 'react';
import { fetchCar, fetchShowroom, getConfiguration } from '../services/api.js';
import CarWizardConfiguratorNew from './CarWizardConfiguratorNew';
import CarWizardLegacy from './CarWizardLegacy';
import ModelSplash from './carWizard/ModelSplash';
import { isLegacyCar } from './carWizard/carModel';
import type { ShowroomItem } from './carWizard/carWizardTypes';
import { IS_PREVIEW } from '../config.js';

interface Props {
  containerId?: string;
  carData?: any;
  embedded?: boolean;
}

type Hydration = {
  selections: Record<string, string>;
  subSelections: Record<string, string>;
  overlayIds?: string[];
  sceneryId?: string | null;
} | null;

type Route =
  | { kind: 'share'; slug: string }
  | { kind: 'car'; carId: string; createdAt: string; pinned: boolean } // pinned = fixed by the embed
  | { kind: 'splash' };

// Shared build: ?c=:slug on the embedding page. /c/:slug is still read for
// links sent before the switch (if the host redirects them here).
const getShareSlug = () => {
  const m = window.location.pathname.match(/\/c\/([A-Za-z0-9]+)/);
  if (m) return m[1];
  try {
    const q = new URLSearchParams(window.location.search).get('c');
    if (q && /^[A-Za-z0-9]+$/.test(q)) return q;
  } catch {}
  return null;
};

// Where to go, from the URL (?carId=&createdAt= or /c/:slug) or the embed's
// data-car-record. Without either, show the model splash.
const readRoute = (containerId: string): Route => {
  const slug = getShareSlug();
  if (slug) return { kind: 'share', slug };
  try {
    const params = new URLSearchParams(window.location.search);
    const carId = params.get('carId');
    const createdAt = params.get('createdAt');
    if (carId && createdAt) return { kind: 'car', carId, createdAt, pinned: false };
  } catch {}
  const container = document.getElementById(containerId);
  const record = container?.getAttribute('data-car-record');
  if (record) {
    const [carId, createdAt] = record.split('|');
    if (carId && createdAt) return { kind: 'car', carId, createdAt, pinned: true };
  }
  return { kind: 'splash' };
};

const setCarQuery = (car: { id: string; createdAt: string } | null, replace = false) => {
  const url = new URL(window.location.href);
  if (car) {
    url.searchParams.set('carId', car.id);
    url.searchParams.set('createdAt', car.createdAt);
  } else {
    url.searchParams.delete('carId');
    url.searchParams.delete('createdAt');
  }
  window.history[replace ? 'replaceState' : 'pushState']({}, '', url.toString());
};

const CarConfigurator: React.FC<Props> = ({ containerId = 'root', carData, embedded = false }) => {
  const [route, setRoute] = useState<Route>(() => readRoute(containerId));
  const [raw, setRaw] = useState<any>(carData ?? null);
  const [showroom, setShowroom] = useState<ShowroomItem[] | null>(null);
  const [loading, setLoading] = useState(!carData);
  const [error, setError] = useState<string | null>(null);
  const [hydration, setHydration] = useState<Hydration>(null);

  // Browser back/forward between the splash and a car.
  useEffect(() => {
    const onPop = () => setRoute(readRoute(containerId));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [containerId]);

  useEffect(() => {
    if (carData) { setRaw(carData); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        if (route.kind === 'share') {
          // Save-&-share deep link: /c/:slug → load configuration + its car.
          const { configuration, car } = await getConfiguration(route.slug, true);
          if (!car) throw new Error('Shared configuration is missing its car');
          const selections: Record<string, string> = {};
          const subSelections: Record<string, string> = {};
          (configuration.selections || []).forEach((sel: any) => {
            selections[sel.stepId] = sel.optionId;
            if (sel.subOptionId) subSelections[sel.stepId] = sel.subOptionId;
          });
          if (!cancelled) {
            setHydration({
              selections,
              subSelections,
              overlayIds: configuration.activeOverlayIds || [],
              sceneryId: configuration.sceneryId ?? null,
            });
            setRaw(car);
          }
          return;
        }

        if (route.kind === 'car') {
          const car = await fetchCar(route.carId, route.createdAt);
          if (!cancelled) { setHydration(null); setRaw(car); }
          return;
        }

        // Splash: a single published car skips straight to its configurator.
        const items: ShowroomItem[] = await fetchShowroom();
        if (cancelled) return;
        if (items.length === 0) {
          throw new Error(IS_PREVIEW
            ? 'Preview: no car is published yet. In the admin, load a car, tick “Show in model picker” and save.'
            : 'No models are available right now.');
        }
        if (items.length === 1) {
          setCarQuery({ id: items[0].id, createdAt: items[0].createdAt }, true);
          setRoute({ kind: 'car', carId: items[0].id, createdAt: items[0].createdAt, pinned: true });
          return;
        }
        setRaw(null);
        setShowroom(items);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Failed to load car configuration');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [route, carData]);

  const pickModel = useCallback((car: ShowroomItem) => {
    setCarQuery({ id: car.id, createdAt: car.createdAt });
    setRoute({ kind: 'car', carId: car.id, createdAt: car.createdAt, pinned: false });
    try { window.scrollTo({ top: 0, behavior: 'auto' }); } catch {}
  }, []);

  const backToSplash = useCallback(() => {
    setCarQuery(null);
    setRoute({ kind: 'splash' });
  }, []);

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-white ${embedded ? 'h-64' : 'min-h-screen'}`}>
        <div className="text-center">
          <div className="loading-spinner mb-4"></div>
          <p className="text-gray-600">Loading car configuration...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-white ${embedded ? 'h-64' : 'min-h-screen'}`}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  if (route.kind === 'splash' && showroom && !carData) {
    return <ModelSplash cars={showroom} onPick={pickModel} embedded={embedded} />;
  }

  if (!raw) return null;

  // "Change model" only when the car was chosen from the splash (not pinned by
  // the embed and not a shared build).
  const canChangeModel = !carData && route.kind === 'car' && !route.pinned;

  return isLegacyCar(raw)
    ? <CarWizardLegacy key={raw.id} carData={raw} embedded={embedded} />
    : <CarWizardConfiguratorNew
        key={raw.id}
        carData={raw}
        embedded={embedded}
        initialSelections={hydration?.selections}
        initialSubSelections={hydration?.subSelections}
        initialOverlayIds={hydration?.overlayIds}
        initialSceneryId={hydration?.sceneryId}
        startInSummary={!!hydration}
        onChangeModel={canChangeModel ? backToSplash : undefined}
      />;
};

export default CarConfigurator;
